import { Eye, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RequestCommissionEditDialog } from "@/pages/commission/RequestCommissionEditDialog";
import type { CommissionTransaction } from "@/lib/commission.api";
import type { Party } from "@/lib/party.api";
import { getPartyName } from "../commission-calculations";
import { formatDate, formatMoney, getCommissionAmount } from "../commission-formatters";
import { CommissionPendingBadge } from "./CommissionPendingBadge";

interface CommissionGridProps {
  commissions: CommissionTransaction[];
  parties: Record<number, Party>;
  canDelete: boolean;
  deletingId: number | null;
  typeLabel: (code: string) => string;
  onSelect: (commission: CommissionTransaction) => void;
  onReload: () => void;
  onDelete: (id: number) => void;
}

export function CommissionGrid({
  commissions,
  parties,
  canDelete,
  deletingId,
  typeLabel,
  onSelect,
  onReload,
  onDelete,
}: CommissionGridProps) {
  if (commissions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucune commission.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {commissions.map((commission) => {
        const client = getPartyName(parties, commission.clientPartyId);
        const referrer = getPartyName(parties, commission.referrerPartyId);
        return (
          <div key={commission.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-foreground">
                  {typeLabel(commission.type)}
                </p>
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                  #{commission.id} - {formatDate(commission.date)}
                </p>
              </div>
              {commission.pendingEditRequestId && <CommissionPendingBadge />}
            </div>

            <p className="mt-3 text-[20px] font-bold tabular-nums text-foreground">
              {formatMoney(getCommissionAmount(commission))}
            </p>

            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 text-[11px]">
              <div className="min-w-0">
                <p className="text-subtle">Client</p>
                <p className="mt-0.5 truncate font-medium text-body">{client ?? "-"}</p>
              </div>
              <div className="min-w-0">
                <p className="text-subtle">Référent</p>
                <p className="mt-0.5 truncate font-medium text-body">{referrer ?? "-"}</p>
              </div>
            </div>

            {commission.note && (
              <p className="mt-3 truncate rounded border border-border bg-surface-muted px-3 py-2 text-[11.5px] text-body">
                {commission.note}
              </p>
            )}

            <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
              <Button size="sm" variant="ghost" onClick={() => onSelect(commission)}>
                <Eye size={13} /> Détail
              </Button>
              <div className="flex items-center gap-1">
                {commission.pendingEditRequestId ? (
                  <span className="px-2 py-0.5 text-[10px] font-semibold text-warning-text">
                    En attente
                  </span>
                ) : (
                  <RequestCommissionEditDialog commission={commission} onRequested={onReload} />
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(commission.id)}
                    className="text-danger-text hover:bg-danger-bg"
                    title="Supprimer"
                    disabled={deletingId === commission.id}
                  >
                    {deletingId === commission.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Trash2 size={13} />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
